import {ReactElement, useMemo, useRef} from 'react';

import {Swiper, SwiperSlide} from '../../swiper';
import SwiperCore, {Navigation, Autoplay} from 'swiper';
import {EventImageType} from '../../types';
import classNames from 'classnames';
import {EventPlaceholderDesktopPNG} from '../../assets/img/index';
import ImageContainer from '../ImageContainer.client';

SwiperCore.use([Navigation, Autoplay]);

interface EventImagesCarouselProps {
  eventImages: Array<EventImageType>;
}

export default function EventImagesCarousel({
  eventImages,
}: EventImagesCarouselProps): ReactElement {
  const prevNavRef = useRef(null);
  const nextNavRef = useRef(null);
  const images = useMemo(
    () =>
      eventImages?.length ?? 0 > 0
        ? eventImages
        : [{uuid: 'placeholder'} as EventImageType],
    [eventImages],
  );
  const hasMultipleSlides = images.length > 1;

  return (
    <div className="hero-slider-outer hero-slider-only-image">
      <Swiper
        id="hero-slider"
        className="hero-slider"
        spaceBetween={0}
        slidesPerView={1}
        loop
        autoplay={
          hasMultipleSlides && {
            delay: 6000,
            disableOnInteraction: false,
            pauseOnMouseEnter: true,
          }
        }
        allowTouchMove={hasMultipleSlides}
        navigation={{
          prevEl: prevNavRef.current,
          nextEl: nextNavRef.current,
        }}
        onBeforeInit={({params: {navigation}}: SwiperCore) => {
          if (navigation && typeof navigation !== 'boolean') {
            navigation.prevEl = prevNavRef.current;
            navigation.nextEl = nextNavRef.current;
          }
        }}
      >
        <div className="hero-slider swiper-wrapper">
          {images.map(
            ({
              image_desktop_webp,
              image_desktop_jpeg,
              image_mobile_webp,
              image_mobile_jpeg,
              uuid,
            }) => (
              <SwiperSlide
                className="hero-slide swiper-slide hero-slide-mask"
                key={uuid}
              >
                <Slide
                  imageDesktopWebp={image_desktop_webp}
                  imageDesktopJpeg={image_desktop_jpeg}
                  imageMobileWebp={image_mobile_webp}
                  imageMobileJpeg={image_mobile_jpeg}
                />
              </SwiperSlide>
            ),
          )}
        </div>
        <div
          className={classNames('hero-slider__controls container', {
            hidden: !hasMultipleSlides,
          })}
        >
          <div
            className="hero-slider__control carousel-control-left"
            ref={prevNavRef}
            id="prev-hero-slide"
          >
            <span className="icon-arrow-left" />
          </div>
          <div
            className="hero-slider__control carousel-control-right"
            ref={nextNavRef}
            id="next-hero-slide"
          >
            <span className="icon-arrow-right" />
          </div>
        </div>
      </Swiper>
    </div>
  );
}

interface SlideProps {
  imageDesktopWebp: string;
  imageDesktopJpeg: string;
  imageMobileWebp: string;
  imageMobileJpeg: string;
}

function Slide({
  imageDesktopWebp,
  imageDesktopJpeg,
  imageMobileWebp,
  imageMobileJpeg,
}: SlideProps) {
  return (
    <>
      <div className="hero-slide-d">
        <ImageContainer
          jpegSrc={imageDesktopJpeg || EventPlaceholderDesktopPNG}
          webpSrc={imageDesktopWebp}
          widthInPx={800}
          heightInPx={800}
          alt="Whisky event image"
        />
      </div>
      <div className="hero-slide-m">
        <ImageContainer
          jpegSrc={imageMobileJpeg || EventPlaceholderDesktopPNG}
          webpSrc={imageMobileWebp}
          widthInPx={800}
          heightInPx={800}
          alt="Whisky event image"
        />
      </div>
    </>
  );
}
